// Estudos › Relatório de estudos (0.65, Texto 3 E): tempo por matéria na semana escolhida (com a meta),
// os 7 dias da semana e as últimas 8 semanas.
// 0.72 (texto 11e D): sem mudar o visual, acrescenta: total do período com meta somada, estudado e faltou; em cada matéria
// meta, estudado, faltou (vermelho) ou superou (verde) e porcentagem; filtro por fichário; Semana/Mês com ◀ ▶ para
// períodos anteriores; "Exportar em PDF" (janela de compartilhar + histórico de relatórios da aba, padrão E1 do texto 03).
// Metas convertidas para o período: meta por dia × dias do período; meta por semana ÷ 7 × dias do período.
import { useState } from 'react';
import { Alert, Pressable, ScrollView, Text, View } from 'react-native';
import { useDoc } from '../store/store';
import { metaNoPeriodo, segundaDe } from '../store/estudos';
import { MES_CURTO, addDays, brYmd, nomeMes, parseYmd, today, ymd } from '../lib/date';
import { durHoras as durLabel } from '../lib/time';   // 0.69 (I): metas e tempos como "21h", "5h", "1h30" (não "21:00", que parece horário)
import { N_, t } from '../lib/i18n';
import { gerarECompartilharPdf, type BlocoPdf } from '../lib/pdf';
import { marcarAbaAtual } from '../lib/relatorios';
import { Btn, Chip, Sheet } from './ui';
import { RelatoriosGerados } from './RelatoriosGerados';
import { Barras, CartaoTotal, NavPeriodo } from './relatorio/Periodo';
import { C, alpha } from './theme';

const DIAS = [N_('Seg'), N_('Ter'), N_('Qua'), N_('Qui'), N_('Sex'), N_('Sáb'), N_('Dom')];
type Modo = 'semana' | 'mes';
const inicioMes = (s: string) => s.slice(0, 8) + '01';
const fimMes = (s: string) => { const d = parseYmd(inicioMes(s)); return ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0)); };
const somaMes = (s: string, n: number) => { const d = parseYmd(inicioMes(s)); return ymd(new Date(d.getFullYear(), d.getMonth() + n, 1)); };
const inicioDe = (modo: Modo, s: string) => (modo === 'semana' ? segundaDe(s) : inicioMes(s));

export function RelatorioEstudos({ visivel, cor, onFechar }: { visivel: boolean; cor: string; onFechar: () => void }) {
  const { ficharios, sessoes: todas } = useDoc('estudos');
  const [modo, setModo] = useState<Modo>('semana');
  const [ini, setIni] = useState(() => segundaDe(today()));
  const [fid, setFid] = useState<string | null>(null);   // filtro por fichário (null = todos)
  const [gerando, setGerando] = useState(false);
  const semana = modo === 'semana';
  const fim = semana ? addDays(ini, 6) : fimMes(ini);
  const nDias = Math.round((parseYmd(fim).getTime() - parseYmd(ini).getTime()) / 86400000) + 1;
  const filtro = fid ? ficharios.find(f => f.id === fid) ?? null : null;
  const doFiltro = filtro ? new Set(filtro.materias.map(m => m.id)) : null;
  const sessoes = doFiltro ? todas.filter(s => doFiltro.has(s.materiaId)) : todas;
  const minutos = (de: string, ate: string) => sessoes.reduce((a, s) => a + (s.data >= de && s.data <= ate ? s.min : 0), 0);
  const porMat: Record<string, number> = {};
  for (const s of sessoes) if (s.data >= ini && s.data <= fim) porMat[s.materiaId] = (porMat[s.materiaId] ?? 0) + s.min;
  const total = Object.values(porMat).reduce((a, b) => a + b, 0);
  const materias = (filtro ? [filtro] : ficharios).flatMap(f => f.materias.map(m => ({ m, f }))).filter(({ m }) => porMat[m.id] || m.metaMin)
    .sort((a, b) => (porMat[b.m.id] ?? 0) - (porMat[a.m.id] ?? 0));
  const linhas = materias.map(({ m, f }) => { const v = porMat[m.id] ?? 0, meta = m.metaMin ? metaNoPeriodo(m, nDias) : 0; return { m, f, v, meta, falta: Math.max(0, meta - v) }; });
  const metaTotal = linhas.reduce((a, x) => a + x.meta, 0);
  const faltouTotal = linhas.reduce((a, x) => a + x.falta, 0);
  const porDia = Array.from({ length: nDias }, (_, i) => { const d = addDays(ini, i); return sessoes.filter(s => s.data === d).reduce((a, s) => a + s.min, 0); });
  // embaixo: últimas 8 semanas (Semana) ou últimos 6 meses (Mês); tocar escolhe o período
  const historico = semana
    ? Array.from({ length: 8 }, (_, i) => { const s = addDays(ini, -7 * (7 - i)); return { s, min: minutos(s, addDays(s, 6)), rot: brYmd(s).slice(0, 5) }; })
    : Array.from({ length: 6 }, (_, i) => { const s = somaMes(ini, i - 5); return { s, min: minutos(s, fimMes(s)), rot: t(MES_CURTO[parseYmd(s).getMonth()]) }; });
  const maxHist = Math.max(1, ...historico.map(x => x.min));
  const atual = ini === inicioDe(modo, today());
  const nomeMesAno = (s: string) => { const d = parseYmd(s); return t('{mes} de {ano}', { mes: nomeMes(d.getMonth()), ano: d.getFullYear() }); };
  const titulo = semana ? (atual ? t('Esta semana') : t('Semana de {d}', { d: brYmd(ini) })) : (atual ? t('Este mês') : nomeMesAno(ini));
  const mudarModo = (m: Modo) => { if (m === modo) return; setModo(m); setIni(inicioDe(m, m === 'semana' && ini === inicioMes(today()) ? today() : ini)); };
  const andar = (n: number) => setIni(semana ? addDays(ini, 7 * n) : somaMes(ini, n));
  const resultado = (meta: number, v: number) => (v >= meta ? (v > meta ? t('superou {dur}', { dur: durLabel(v - meta) }) : t('meta cumprida')) : t('faltou {dur}', { dur: durLabel(meta - v) }));

  const exportar = async () => {
    if (gerando) return;
    setGerando(true);
    try {
      const periodo = `${brYmd(ini)} – ${brYmd(fim)}`;
      const blocos: BlocoPdf[] = [
        { t: 'titulo', texto: t('Relatório de estudos') },
        { t: 'sub', texto: `${titulo} · ${periodo} · ${filtro ? filtro.nome : t('Todos os fichários')}` },
        { t: 'linha' },
        { t: 'par', negrito: true, texto: t('Estudado: {dur}', { dur: durLabel(total) }) },
        ...(metaTotal ? [{ t: 'par', texto: t('Meta somada: {dur}', { dur: durLabel(metaTotal) }) } as BlocoPdf,
          { t: 'par', texto: t('Faltou: {dur}', { dur: durLabel(faltouTotal) }) } as BlocoPdf] : []),
        { t: 'espaco', h: 6 },
        { t: 'par', negrito: true, texto: t('Por matéria') },
        linhas.length
          ? { t: 'tabela', colunas: [t('Matéria'), t('Fichário'), t('Meta'), t('Estudado'), t('Resultado'), '%'], larguras: [0.25, 0.2, 0.12, 0.13, 0.2, 0.1],
              linhas: linhas.map(x => [x.m.nome, x.f.nome, x.meta ? durLabel(x.meta) : '—', durLabel(x.v), x.meta ? resultado(x.meta, x.v) : '—', x.meta ? `${Math.round((x.v / x.meta) * 100)}%` : '—']) }
          : { t: 'par', texto: semana ? t('Nenhum estudo registrado nesta semana.') : t('Nenhum estudo registrado neste mês.') },
        { t: 'espaco', h: 6 },
        { t: 'par', negrito: true, texto: t('Por dia') },
        { t: 'tabela', colunas: [t('Dia'), t('Tempo')], larguras: [0.5, 0.5], linhas: porDia.map((v, i) => [brYmd(addDays(ini, i)), v ? durLabel(v) : '—']) },
      ];
      marcarAbaAtual('estudos');   // o PDF entra no histórico de relatórios de Estudos
      await gerarECompartilharPdf(`${t('Relatório de estudos')} ${brYmd(ini)} a ${brYmd(fim)}${filtro ? ' ' + filtro.nome : ''}`, blocos, 'My Planner');
    } catch (e: any) {
      Alert.alert(t('Não foi possível gerar o PDF.'), String(e?.message ?? e));
    } finally { setGerando(false); }
  };

  return (
    <Sheet visible={visivel} onClose={onFechar} title={t('Relatório de estudos')}>
      <ScrollView contentContainerStyle={{ padding: 16, gap: 14, paddingBottom: 60 }}>
        <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center' }}>
          <Chip small cor={cor} on={semana} label={t('Semana')} onPress={() => mudarModo('semana')} />
          <Chip small cor={cor} on={!semana} label={t('Mês')} onPress={() => mudarModo('mes')} />
        </View>
        {ficharios.length > 0 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
            <Chip small cor={cor} on={!filtro} label={t('Todos os fichários')} onPress={() => setFid(null)} />
            {ficharios.map(f => <Chip key={f.id} small cor={f.cor ?? cor} on={filtro?.id === f.id} label={`${f.icon} ${f.nome}`} onPress={() => setFid(f.id)} />)}
          </ScrollView>
        )}
        <NavPeriodo cor={cor} titulo={titulo} datas={`${brYmd(ini)} – ${brYmd(fim)}`} onAnterior={() => andar(-1)} onProximo={() => andar(1)} podeProximo={!atual} />
        <CartaoTotal valor={durLabel(total)} legenda={semana ? t('estudados na semana') : t('estudados no mês')}>
          {/* D1: total do período com a meta somada, o estudado e o que faltou */}
          {metaTotal > 0 && (
            <View style={{ flexDirection: 'row', alignSelf: 'stretch', marginTop: 12, paddingTop: 10, borderTopWidth: 1, borderTopColor: C.card2 }}>
              {[[t('Meta somada'), durLabel(metaTotal), C.text], [t('Estudado'), durLabel(total), C.text],
                [t('Faltou'), durLabel(faltouTotal), faltouTotal ? C.danger : C.ok]].map(([rot, val, c]) => (
                <View key={rot} style={{ flex: 1, alignItems: 'center' }}>
                  <Text style={{ color: c, fontWeight: '800', fontSize: 16 }}>{val}</Text>
                  <Text style={{ color: C.sub, fontSize: 11 }}>{rot}</Text>
                </View>
              ))}
            </View>
          )}
        </CartaoTotal>

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('POR DIA')}</Text>
        <Barras valores={porDia} cor={cor} largas={semana} rotuloValor={durLabel}
          rotulos={porDia.map((_, i) => (semana ? t(DIAS[i]) : (i === 0 || (i + 1) % 5 === 0 ? String(i + 1) : '')))} />

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{t('POR MATÉRIA')}</Text>
        {!linhas.length && <Text style={{ color: C.sub }}>{semana ? t('Nenhum estudo registrado nesta semana.') : t('Nenhum estudo registrado neste mês.')}</Text>}
        {linhas.map(({ m, f, v, meta }) => {
          const pct = meta ? Math.min(1, v / meta) : 1;
          return (
            <View key={m.id} style={{ backgroundColor: C.card, borderRadius: 14, padding: 12, gap: 6 }}>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {/* 0.69 (I): nome longo de fichário quebra para a linha de baixo (antes era cortado: "Direito faculd...") */}
                <Text style={{ color: C.text, flex: 1, fontWeight: '600' }}>{m.icon} {m.nome} <Text style={{ color: C.mute, fontSize: 11 }}>· {f.nome}</Text></Text>
                <Text style={{ color: meta && v >= meta ? C.ok : C.sub, fontSize: 12 }}>{meta ? t('{feito} de {meta}', { feito: durLabel(v), meta: durLabel(meta) }) : durLabel(v)}</Text>
              </View>
              <View style={{ height: 6, borderRadius: 3, backgroundColor: C.card2 }}>
                <View style={{ height: 6, borderRadius: 3, width: `${Math.round(pct * 100)}%`, backgroundColor: meta && v >= meta ? C.ok : (f.cor ?? cor) }} />
              </View>
              {/* D2: porcentagem e "faltou" em vermelho ou "superou" em verde */}
              {meta > 0 && (
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Text style={{ color: C.sub, fontSize: 11, flex: 1 }}>{Math.round((v / meta) * 100)}%</Text>
                  <Text style={{ color: v >= meta ? C.ok : C.danger, fontSize: 11, fontWeight: '700' }}>{resultado(meta, v)}</Text>
                </View>
              )}
            </View>
          );
        })}

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '800' }}>{semana ? t('ÚLTIMAS 8 SEMANAS') : t('ÚLTIMOS 6 MESES')}</Text>
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 6, height: 110, backgroundColor: C.card, borderRadius: 16, padding: 12 }}>
          {historico.map(x => (
            <Pressable key={x.s} onPress={() => setIni(x.s)} style={{ flex: 1, alignItems: 'center', gap: 4 }}>
              <View style={{ width: '70%', height: Math.max(2, (x.min / maxHist) * 64), borderRadius: 4, backgroundColor: x.s === ini ? cor : alpha(cor, 0.4) }} />
              <Text style={{ color: C.sub, fontSize: 9 }}>{x.rot}</Text>
            </Pressable>
          ))}
        </View>

        {/* D5: PDF pela janela de compartilhar do sistema; fica no histórico de relatórios de Estudos (logo abaixo) */}
        <Btn cor={cor} outline disabled={gerando} label={'📄 ' + (gerando ? t('Gerando…') : t('Exportar em PDF'))} onPress={exportar} />
        <RelatoriosGerados aba="estudos" cor={cor} />
      </ScrollView>
    </Sheet>
  );
}
